import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { CorsaLogo, TaurusLogo, MelioraLogo, ChespiEnergyLogo, ChespiHospitalityLogo, ChespiRetailLogo, ChespiFarmingLogo } from "./Logo";
import { useLang } from "../i18n";

const IMAGES = {
  residential:
    "https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  commercial: `${process.env.PUBLIC_URL}/FerdinandBusinessCenter.jpeg`,
  tourism: `${process.env.PUBLIC_URL}/imaginePtSectiuneaHospitality.jpg`,
  finance:
    "https://images.pexels.com/photos/20843727/pexels-photo-20843727.jpeg?auto=compress&cs=tinysrgb&w=1400",
  energy:
    "https://images.unsplash.com/photo-1698752822107-69f8973936e4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  retail: `${process.env.PUBLIC_URL}/liqourstore.jpeg`,
  farm:
    "https://images.unsplash.com/photo-1500382017468-9049fed747ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

// Top block — the 3 flagship sub-groups (Meliora / Taurus / Corsa). On lg (12 cols)
// Residential is a 2-row hero on the left; Commercial + Finance stack on the right.
// The 4 divisions below sit in their own even 2×2 grid (see render), so they no
// longer share this 12-col bento.
const SUBGROUP_SPANS = [
  "lg:col-span-6 lg:row-span-2",
  "lg:col-span-6",
  "lg:col-span-6",
];

// "Brand tiles" — these cards drop the photo + copy and instead show just the
// sub-brand's logo centered on its signature page colour (Taurus anthracite,
// Corsa burgundy). Any key listed here is rendered that way.
const BRAND_TILE = {
  residential: "bg-gradient-to-br from-[#6f5744] via-[#5a4434] to-[#47352e]",
  commercial: "bg-[#16181a]",
  finance: "bg-[#5a1a14]",
  energy: "bg-gradient-to-br from-[#065f46] via-[#064e3b] to-[#022c22]",
  retail: "bg-gradient-to-br from-tobacco-light via-tobacco-surface to-tobacco",
  tourism: "bg-gradient-to-br from-[#E7D9BD] via-[#DCC9A4] to-[#CBB489]",
};

// Shared sizing for the centered framed sub-brand lockups (Energy / Retail / Farming),
// scaled up to match the prominence of the Taurus / Corsa / Meliora brand tiles.
const FRAMED_LOGO_PROPS = {
  // Base (mobile) sized to stay big but keep a clean margin inside the tile (the
  // full text-2xl ran edge-to-edge); scales up further on sm/lg.
  textClass: "text-[1.375rem] sm:text-2xl lg:text-3xl",
  // Crest kept ~1.4× the cap height so it reads as the centerpiece without bulging
  // past the words or the frame — the old h-11/h-12/h-14 ran ~2× and crowded on mobile.
  lionClass: "h-8 w-8 sm:h-9 sm:w-9 lg:h-11 lg:w-11",
  // Generous side padding so the words sit clear of the frame borders (they were
  // glued to the edges); the card has ample room around the frame on every width.
  frameClass: "border-2 px-6 py-2.5 sm:px-6 sm:py-2.5 lg:px-4 lg:py-2.5",
  gapClass: "gap-2.5 sm:gap-2.5 lg:gap-3",
  trackingClass: "tracking-[0.1em]",
};

// Hospitality sits on the full-width banner tile — wider but with the long word
// "HOSPITALITY", so it gets its own ramp: small enough to fit narrow mobile / 2-col
// widths, larger on the wide desktop banner. Tighter tracking buys room for the word.
const TOURISM_LOGO_PROPS = {
  // "HOSPITALITY" is the longest word, so the mobile base stays a notch below the
  // others to keep a clean margin; sm is capped by the narrow md 2-col tile; lg fills the banner.
  // Hospitality now sits in the even 2×2 divisions grid (a half-width square on lg),
  // not a full-width banner — so lg is brought down to match the other framed lockups.
  textClass: "text-lg sm:text-2xl lg:text-3xl",
  // Same ~1.4× crest-to-cap ratio as the other lockups; the old h-9/h-12 ran ~2× and
  // crowded the long word on the narrow mobile / md 2-col widths.
  lionClass: "h-7 w-7 sm:h-9 sm:w-9 lg:h-12 lg:w-12",
  // Roomy side padding so "HOSPITALITY" clears the borders; sm stays a notch tighter
  // because the md 2-col tile is narrow.
  frameClass: "border-2 px-6 py-2.5 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3",
  gapClass: "gap-2 sm:gap-2.5 lg:gap-3",
  trackingClass: "tracking-[0.06em]",
  // White lockup on the light-beige tile. White-on-beige is naturally low contrast,
  // so a soft warm drop-shadow lifts the wordmark + crest + frame off the background.
  textColorClass: "text-white",
  borderColorClass: "border-white",
  lionFilterClass: "brightness-0 invert",
  className: "drop-shadow-[0_1px_6px_rgba(45,30,12,0.55)]",
};

// A single sector card. `i` is the global index (drives the 01–07 corner number);
// `onOpen` navigates; `soonLabel` is the localized "Coming Soon" pill text.
function SectorTile({ s, i, onOpen, soonLabel }) {
  return (
    <article
      data-testid={`sector-card-${s.k}`}
      onClick={() => onOpen(s.k)}
      className={`group relative h-full w-full cursor-pointer overflow-hidden border border-gold/15 transition-colors duration-500 hover:border-gold/45 ${
        BRAND_TILE[s.k] || ""
      }`}
    >
      {!BRAND_TILE[s.k] && (
        <>
          <img
            src={IMAGES[s.k]}
            alt={s.t}
            loading="lazy"
            className={`absolute inset-0 h-full w-full object-cover transition-all [transition-duration:1400ms] ease-out ${
              s.soon
                ? "scale-105 grayscale blur-md"
                : "grayscale-[0.35] group-hover:scale-[1.07] group-hover:grayscale-0"
            }`}
          />
          <div
            className={`absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/10 transition-opacity duration-500 ${
              s.soon ? "from-ink/90 via-ink/70" : "group-hover:from-ink/95"
            }`}
          />
        </>
      )}

      {s.k === "residential" && (
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 scale-95 transition-transform duration-500 group-hover:scale-100 sm:scale-100 sm:group-hover:scale-105">
          <MelioraLogo />
        </div>
      )}

      {s.k === "finance" && (
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 scale-95 transition-transform duration-500 group-hover:scale-100 sm:scale-100 sm:group-hover:scale-105">
          <CorsaLogo />
        </div>
      )}

      {s.k === "commercial" && (
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 scale-95 transition-transform duration-500 group-hover:scale-100 sm:scale-100 sm:group-hover:scale-105">
          <TaurusLogo />
        </div>
      )}

      {(s.k === "energy" || s.k === "retail" || s.k === "farm" || s.k === "tourism") && (
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 scale-95 transition-transform duration-500 group-hover:scale-100 sm:scale-100 sm:group-hover:scale-105">
          {s.k === "energy" ? (
            <ChespiEnergyLogo {...FRAMED_LOGO_PROPS} />
          ) : s.k === "retail" ? (
            <ChespiRetailLogo {...FRAMED_LOGO_PROPS} />
          ) : s.k === "tourism" ? (
            <ChespiHospitalityLogo {...TOURISM_LOGO_PROPS} />
          ) : (
            <ChespiFarmingLogo {...FRAMED_LOGO_PROPS} />
          )}
        </div>
      )}

      <span className="absolute left-6 top-6 font-serif text-sm tracking-[0.2em] text-gold/70">
        {String(i + 1).padStart(2, "0")}
      </span>
      {!s.soon && (
        <ArrowUpRight
          className="absolute right-6 top-6 text-gold/50 transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-gold"
          size={24}
        />
      )}

      {s.soon && (
        <span className="absolute right-6 top-6 rounded-full border border-gold/40 bg-ink/40 px-4 py-1.5 text-[10px] font-medium uppercase tracking-[0.28em] text-gold backdrop-blur-sm">
          {soonLabel}
        </span>
      )}

      {!BRAND_TILE[s.k] && s.k !== "farm" && (
        <div className="relative flex h-full flex-col justify-end p-7">
          <span className="mb-3 h-px w-8 origin-left bg-gold/60 transition-all duration-500 group-hover:w-14" />
          {s.k === "tourism" ? (
            <ChespiHospitalityLogo className="max-w-full self-start" />
          ) : (
            <h3 className="font-serif text-2xl text-white lg:text-3xl">{s.t}</h3>
          )}
          <p className="mt-3 max-w-md text-sm leading-relaxed text-white/55 transition-colors duration-500 group-hover:text-white/75">
            {s.d}
          </p>
        </div>
      )}
    </article>
  );
}

export default function Sectors() {
  const { t } = useLang();
  const navigate = useNavigate();
  // Only sectors with a real page are navigable; the rest stay inert.
  const READY = ["residential", "commercial", "finance", "retail", "energy", "tourism"];
  const openSector = (k) => {
    if (READY.includes(k)) navigate(`/sectors/${k}`);
  };
  // Homepage display order (the i18n order and navbar dropdown stay untouched):
  // Residential (hero) · Commercial · Finance · Hospitality (banner) · Energy · Retail · Farming.
  const ORDER = ["residential", "commercial", "finance", "tourism", "energy", "retail", "farm"];
  const items = ORDER.map((k) => t.sectors.items.find((s) => s.k === k)).filter(Boolean);
  // The 3 flagship sub-groups on top, the 4 Chespi divisions below, parted by a fine rule.
  const subgroups = items.slice(0, 3);
  const divisions = items.slice(3);
  return (
    <section id="sectors" className="section-pad relative bg-ink">
      <div className="container-luxe">
        <Reveal className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="kicker mb-5">{t.sectors.overline}</p>
            <h2 className="font-serif text-3xl font-light leading-tight text-white sm:text-4xl lg:text-5xl">{t.sectors.title}</h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-white/50 md:text-base">{t.sectors.subtitle}</p>
        </Reveal>

        {/* Top block — the 3 flagship sub-groups (Meliora / Taurus / Corsa). */}
        <div className="grid auto-rows-[280px] grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-12">
          {subgroups.map((s, i) => (
            <Reveal key={s.k} delay={(i % 3) * 0.08} className={SUBGROUP_SPANS[i]}>
              <SectorTile s={s} i={i} onOpen={openSector} soonLabel={t.sectors.soon} />
            </Reveal>
          ))}
        </div>

        {/* Fine divider — parts the 3 sub-groups above from the 4 Chespi divisions below. */}
        <div className="my-10 flex items-center gap-4 lg:my-14" aria-hidden="true">
          <span className="h-px flex-1 bg-gradient-to-r from-transparent to-gold/25" />
          <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-gold/50" />
          <span className="h-px flex-1 bg-gradient-to-l from-transparent to-gold/25" />
        </div>

        {/* Bottom block — the 4 Chespi divisions as even 2×2 squares on tablet/desktop. */}
        <div className="grid auto-rows-[280px] grid-cols-1 gap-4 md:grid-cols-2">
          {divisions.map((s, i) => (
            <Reveal key={s.k} delay={(i % 3) * 0.08}>
              <SectorTile s={s} i={i + 3} onOpen={openSector} soonLabel={t.sectors.soon} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
